<script setup lang="ts">
import { faBellPlus, faBell } from '@fortawesome/pro-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faAngleLeft,
    faCircle1,
    faHexagonExclamation,
    faMessageCheck,
    faTelescope,
} from '@fortawesome/pro-regular-svg-icons';
import RouteInfo from '@components/util/trip/TripCardSegment/RouteInfo.vue';
import type TripPackage from '@itinerary/trip/TripPackage';
import { type CorporationInfo } from '@itinerary/trip/types';
import { PriceTrackType, type TrackScope } from '@store/priceTrack';
import { ReserveType } from '@utils/itinerary';
import Bolt from '@files/bolt.svg?component';
import SuccessIcon from '@files/success.svg?component';
import type Trip from '@itinerary/trip/Trip';
import useAutoReserveModal from '@composable/useAutoReserveModal';
import { type TripSearchForm } from '@search/trip/TripSearchForm';
import type TripModule from '@itinerary/trip/TripModule';

interface IProps {
    trackingType: PriceTrackType;
    tripPackage?: TripPackage;
    price: number;
    trackScope: TrackScope;
}

const { price, trackingType, trackScope, tripPackage } = defineProps<IProps>();
const emit = defineEmits<{
    close: [];
}>();
const itineraryStore = useItineraryStore();
const priceTrackStore = usePriceTrackStore();
const router = useRouter();

const currentModule = useCurrentItineraryModule<TripModule>();

const currentSearchForm = useCurrentSearchForm<TripSearchForm>();

const date = computed(() =>
    currentSearchForm.value.departureDate
        ? dayConfig.js(currentSearchForm.value.departureDate).format(
              formatter({
                  format: 'long',
                  includeWeekday: true,
                  includeYear: true,
              })
          )
        : ''
);

const descriptions = [
    {
        icon: faTelescope,
        description:
            'مِستربلیط تا زمان حرکت به طور مرتب، افزایش ظرفیت را بررسی و در صورت وجود حداقل یک ظرفیت خالی به شما اطلاع‌رسانی خواهد کرد.',
    },
    {
        icon: faMessageCheck,
        description:
            ' اطلاع‌رسانی‌ها از طریق پیامک یا نوتیفیکیشن انجام خواهد شد.',
    },
    {
        icon: faCircle1,
        description: 'افزایش ظرفیت تنها یک‌بار به شما اطلاع داده خواهد شد.',
    },
    {
        icon: faHexagonExclamation,
        description:
            'این اطلاع‌رسانی، به منزله رزرو بلیط برای شما نیست و نیاز است هرچه سریع‌تر جهت رزرو نهایی به مِستربلیط مراجعه فرمایید.',
    },
];
const state = ref<'register' | 'loading' | 'success'>('register');
const isCapacityTracking = computed(
    () => trackingType === PriceTrackType.Capacity
);
const showAutoReserve = computed(
    () =>
        currentSearchForm.value.reserveInfo.type === ReserveType.Flight &&
        itineraryStore.hasAutoReserve &&
        trackingType === PriceTrackType.Capacity
);
const isLoading = computed(() => state.value === 'loading');
const isRegistering = computed(() => state.value === 'register');
const isSuccess = computed(() => state.value === 'success');

const isPreSell = computed(() => trackingType === PriceTrackType.PreSell);
const cid = computed(() => {
    switch (trackingType) {
        case PriceTrackType.Price:
            return `price-tracking-${trackScope}`;
        case PriceTrackType.Capacity:
            return `capacity-tracking-${trackScope}`;
        case PriceTrackType.PreSell:
            return `capacity-tracking-${trackScope}-presell`;
        default:
            return undefined;
    }
});
const title = computed(() => {
    switch (trackingType) {
        case PriceTrackType.Capacity:
            return 'رهگیری ظرفیت';
        case PriceTrackType.Price:
            return 'رهگیری قیمت';
        case PriceTrackType.PreSell:
            return 'اطلاع‌رسانی پیش‌فروش قطار';
        default:
            return '';
    }
});
const modalTitle = computed(() => {
    if (isSuccess.value) {
        return `${title.value} ${trackScopeTitle.value} با موفقیت ثبت شد!`;
    } else {
        switch (trackingType) {
            case PriceTrackType.PreSell:
                return 'اطلاع از آغاز پیش‌فروش قطار';
            case PriceTrackType.Price:
                return 'ارزان شد خبرم کن.';
            case PriceTrackType.Capacity:
                return 'موجود شد خبرم کن.';
        }
    }
});
const infoTitle = computed(() => `${title.value} چطور کار می‌کند`);
const successText = computed(() => {
    if (trackingType === PriceTrackType.PreSell) {
        return 'در صورت آغاز پیش فروش قطار، به شما اطلاع داده خواهد شد.';
    }
    if (isCapacityTracking.value) {
        return `در صورتی که ظرفیت ${trackScopeTitle.value} افزایش یافت به شما اطلاع داده خواهد شد.`;
    } else {
        return `در صورتی که قیمت ${trackScopeTitle.value} کاهش یافت به شما اطلاع داده خواهد شد.`;
    }
});
const bell = computed(() => {
    const result = {
        color: '',
        icon: undefined as IconDefinition | undefined,
    };
    switch (trackingType) {
        case PriceTrackType.Capacity:
            result.icon = faBellPlus;
            break;
        case PriceTrackType.Price:
            result.icon = faBell;
            break;
        default:
            break;
    }
    switch (trackScope) {
        case 'path':
            result.color = 'var(--yellow-500)';
            break;
        case 'ticket':
            result.color = 'var(--blue-400)';
            break;
        default:
            break;
    }
    return result;
});
const trackScopeTitle = computed(() => {
    if (isPreSell.value) {
        return '';
    }
    switch (trackScope) {
        case 'path':
            return 'مسیر';
        case 'ticket':
            return 'بلیط';
        default:
            return '';
    }
});
const segments = computed(
    () =>
        tripPackage?.segments.map(segment => {
            const corporationInfo = segment.trips
                .map(t => t.corporationInfo)
                .reduce((all, c) => {
                    if (!all.some(a => a.title === c.title)) {
                        return [...all, c];
                    }
                    return all;
                }, [] as CorporationInfo[]);
            return {
                ...segment,
                departureDateTime: segment.departureDateTime,
                arrivalDateTime: segment.arrivalDateTime,
                durationDays: segment.durationDays,
                firstTrip: segment.firstTrip,
                lastTrip: segment.lastTrip,
                digest: segment.digest,
                duration: segment.duration,
                origin: segment.origin,
                destination: segment.destination,
                arrivalTime: segment.arrivalTime,
                departureTime: segment.departureTime,
                trips: segment.trips.map(
                    trip =>
                        ({
                            ...trip,
                            dateString: dayConfig
                                .js(trip.departureDateTime)
                                .format(
                                    formatter({
                                        format: 'long',
                                        includeWeekday: true,
                                        includeYear: true,
                                    })
                                ),
                        }) as Trip
                ),
                corporationInfo:
                    corporationInfo.length > 1
                        ? 'چند ایرلاین'
                        : `${corporationInfo[0].title}${
                              corporationInfo[0].subTitle
                                  ? ` ${corporationInfo[0].subTitle}`
                                  : ''
                          }`,
            };
        }) || []
);
const cancel = (close = true) => {
    priceTrackStore.priceTrackInfo = null;
    if (close) {
        emit('close');
    }
    setTimeout(() => {
        state.value = 'register';
    }, 500);
};

const { openAutoReserveModal } = useAutoReserveModal();
const openAutoReserve = () => {
    emit('close');
    nextTick(() => openAutoReserveModal('price-track-click'));
};
const submit = async () => {
    state.value = 'loading';
    try {
        if (isPreSell.value) {
            priceTrackStore.addPreSellAlert(
                `${currentSearchForm.value.originCode}-${currentSearchForm.value.destinationCode}`
            );
        } else {
            await priceTrackStore.addPriceTrack();
        }

        state.value = 'success';
    } catch (e) {
        handleException(e);
        cancel();
    } finally {
        if (!isPreSell.value) {
            priceTrackStore.loadPriceTracks();
        }
    }
};
const goToTracking = () => {
    router.push('/panel/trackings');
    cancel(false);
};
</script>

<template>
    <mr-modal
        :title="modalTitle"
        class="custom-dialog"
        width="auto"
        @close="emit('close')"
    >
        <div v-if="isLoading" class="loading-container">
            <mr-loading />
        </div>
        <template v-else-if="isRegistering">
            <div class="content-container-wrapper">
                <div class="content-container">
                    <div class="header">
                        <mr-icon
                            v-if="bell.icon"
                            :icon="bell.icon"
                            size="large"
                            :color="bell.color"
                        />
                        <mr-typography size="x-large" weight="700" grow>
                            {{ currentModule.originName }} -
                            {{ currentModule.destinationName }}
                        </mr-typography>

                        <mr-price-re
                            v-if="!isPreSell && price"
                            type="small"
                            :price
                        />
                    </div>
                    <mr-divider
                        v-if="!isPreSell"
                        class="mr-divider"
                        :thickness="1"
                    />
                    <div v-if="!isPreSell" class="trip-info-container">
                        <template v-if="trackScope === 'ticket' && tripPackage">
                            <div
                                v-for="(segment, key) in segments"
                                :key
                                class="segment-view"
                            >
                                <mr-typography class="trip-info">
                                    <span>
                                        {{ segment.corporationInfo }}
                                    </span>
                                    <div class="bullet" />
                                    <span>
                                        {{ segment.trips[0].dateString }}
                                    </span>
                                </mr-typography>
                                <div class="route-info">
                                    <div class="location-time-container">
                                        <div class="time">
                                            {{ segment.departureTime }}
                                        </div>
                                        <div class="location">
                                            {{ segment.origin }}
                                        </div>
                                    </div>
                                    <RouteInfo :segment full-width />
                                    <div
                                        class="location-time-container destination"
                                    >
                                        <div class="time">
                                            {{ segment.arrivalTime }}
                                        </div>
                                        <div class="location">
                                            {{ segment.destination }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <div v-else class="segment-view">
                            <mr-typography class="trip-info">
                                <span>{{ date }}</span>
                            </mr-typography>
                        </div>
                    </div>
                </div>
                <mr-accordion
                    v-if="isCapacityTracking"
                    class="info-accordion"
                    :title="infoTitle"
                    is-selected
                    header-position="center"
                >
                    <div
                        v-for="item in descriptions"
                        :key="item.description"
                        class="info-container"
                    >
                        <mr-icon
                            :icon="item.icon"
                            color="var(--cool-gray-700)"
                        />
                        <mr-typography
                            color="var(--cool-gray-700)"
                            class="description"
                        >
                            {{ item.description }}
                        </mr-typography>
                    </div>
                </mr-accordion>
            </div>
        </template>
        <div v-else class="success-container">
            <SuccessIcon />
            <p>
                {{ successText }}
            </p>
            <div
                v-if="showAutoReserve"
                class="auto-reserve"
                @click="openAutoReserve"
            >
                <Bolt />
                <div class="body">
                    <mr-typography
                        size="large"
                        weight="900"
                        color="var(--blue-400)"
                    >
                        از همه جلو بزن!
                    </mr-typography>
                    <mr-typography size="large">
                        با فعال کردن گزینه
                        <mr-typography type="span" size="large" weight="700">
                            «موجود شد رزرو کن»،
                        </mr-typography>
                        با افزایش ظرفیت خالی، شما در اولویت رزرو خواهید بود.
                    </mr-typography>
                </div>
                <mr-icon
                    :icon="faAngleLeft"
                    color="var(--blue-400)"
                    size="x-large"
                />
            </div>
        </div>
        <template #footer>
            <div class="actions">
                <template v-if="isRegistering || isLoading">
                    <mr-button
                        variant="bordered"
                        full-width
                        :loading="isLoading"
                        @click="() => cancel()"
                    >
                        لغو
                    </mr-button>
                    <mr-button
                        :id="cid"
                        full-width
                        :loading="isLoading"
                        @click="submit"
                    >
                        تأیید
                    </mr-button>
                </template>
                <template v-else>
                    <mr-button
                        class="mr-button"
                        full-width
                        variant="bordered"
                        @click="goToTracking"
                    >
                        نمایش رهگیری
                    </mr-button>
                    <mr-button
                        class="mr-button"
                        full-width
                        @click="() => cancel(true)"
                    >
                        متوجه شدم
                    </mr-button>
                </template>
            </div>
        </template>
    </mr-modal>
</template>

<style lang="scss" scoped>
.custom-dialog {
    :deep() {
        .root-container > .content {
            @include desktop-only {
                width: 460px !important;
                .main-wrapper {
                    width: 460px !important;
                }
            }
            display: flex;
            flex-direction: column;
            @include f-gap(12px, column);
            @include mobile-only {
                padding-inline: 12px;
            }
        }
    }
}

.loading-container {
    width: 440px;
    @include mobile-only {
        width: 100%;
    }
}
.content-container-wrapper {
    padding-inline: 20px;
}
.content-container {
    display: flex;
    flex-direction: column;
    border: 1px solid usePalette($p-cool-gray, $p-200);
    border-radius: 16px;
    padding: 12px;
    @include mobile-only {
        margin: 0 16px;
    }
    .mr-divider {
        margin: 12px;
    }
    .header {
        @include f-gap(8px);
        display: flex;
        align-items: center;
        border-radius: 12px 12px 0 0;
    }
}

.trip-info-container {
    border-radius: 0 0 12px 12px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    @include f-gap(16px, column);
    .segment-view {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        @include f-gap(8px, column);
        .trip-info {
            display: flex;
            align-items: center;
            @include f-gap(8px);
            .bullet {
                width: 4px;
                height: 4px;
                border-radius: 100%;
                background-color: black;
            }
        }
        .route-info {
            display: flex;
            align-items: flex-start;
            .location-time-container {
                display: flex;
                flex-direction: column;
                width: 52px;
                &.destination {
                    align-items: flex-end;
                }
                .time {
                    @include tg-xl($w7);
                    display: flex;
                    align-items: center;
                    text-align: right;
                    white-space: nowrap;
                    margin-top: 4px;
                }
                .location {
                    @include tg-s($w5, usePalette($p-cool-gray, $p-600));
                    display: flex;
                    align-items: center;
                    text-align: right;
                    white-space: nowrap;
                }
            }
        }
    }
}
.info-accordion {
    margin-top: 8px;

    :deep() {
        .accordion {
            justify-content: center;
            .acc-title {
                color: usePalette();
                margin-left: 4px;
            }
        }
    }

    .info-container {
        display: flex;
        align-items: center;
        margin: 4px 0;
        @include mobile-only {
            padding: 0 16px;
        }
        .description {
            margin: 0 8px 0 0;
        }
    }
}

.success-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    p {
        @include tg-l($w5);
        margin: 12px 0 0 0;
        text-align: center;
    }
    .auto-reserve {
        display: flex;
        align-items: center;
        padding: 12px 8px;
        border: 1px solid usePalette($p-blue, $p-200);
        border-radius: 16px;
        gap: 8px;
        margin: 12px 20px;
        cursor: pointer;
        background-color: usePalette($p-blue, $p-100);
        &:hover {
            background-color: usePalette($p-blue, $p-200);
        }

        .body {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        }
    }
}

.actions {
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: center;
    @include f-gap(12px);
}
</style>
